import React from "react";
import PropTypes from "prop-types";
import {
  ENROLLMENT_STATUS,
  formatPhoneBR,
  describeNextSend,
  describeEnrolledAt,
} from "../followUpConstants";
import styles from "./EnrollmentItem.module.css";

/**
 * Item da fila ("Quem está na fila"). O backend NÃO traz o nome do contato,
 * só telefone/tipo — então o telefone é o identificador principal, com prefixo
 * "Lead"/"Cliente". Nunca inventamos um nome.
 *
 * Mostra: identidade, nome da cadência, step atual de N, próxima mensagem,
 * progress dots, há quanto tempo entrou, e ações conforme o status.
 */
export default function EnrollmentItem({
  enrollment,
  totalSteps = null,
  onResume,
  onCancel,
  busy = false,
}) {
  const status = enrollment.status;
  const statusInfo = ENROLLMENT_STATUS[status] ?? {
    label: status,
    icon: "•",
    tone: "gray",
  };

  const typeLabel = enrollment.contactType === "LEAD" ? "Lead" : "Cliente";
  const phone = formatPhoneBR(enrollment.contactPhone) || "Sem telefone";
  // Iniciais a partir do telefone (últimos dígitos) — avatar sem nome fabricado.
  const phoneDigits = String(enrollment.contactPhone ?? "").replace(/\D/g, "");
  const avatar = phoneDigits ? phoneDigits.slice(-2) : "?";

  // currentStep = próximo step a enviar (1-based). Steps concluídos = currentStep - 1.
  const currentStep = Number(enrollment.currentStep ?? 1);
  const completed = Math.max(0, currentStep - 1);
  const knownTotal = Number.isFinite(totalSteps) && totalSteps > 0 ? totalSteps : null;

  const next = describeNextSend(enrollment.nextSendAt);
  const showNext = status === "active" || status === "paused";

  const enrolledText = describeEnrolledAt(enrollment.enrolledAt);

  const canResume = status === "paused";
  const canCancel = status === "active" || status === "paused";

  const progressLabel = knownTotal
    ? `Mensagem ${Math.min(currentStep, knownTotal)} de ${knownTotal}`
    : `Mensagem ${currentStep}`;

  return (
    <li className={styles.item}>
      <div className={styles.avatar} aria-hidden="true">
        {avatar}
      </div>

      <div className={styles.main}>
        <div className={styles.identity}>
          <span className={`${styles.typeTag} ${styles[`type${enrollment.contactType === "LEAD" ? "Lead" : "Customer"}`]}`}>
            {typeLabel}
          </span>
          <span className={styles.phone}>
            <span aria-hidden="true">📱</span> {phone}
          </span>
        </div>

        {enrollment.sequence?.name && (
          <p className={styles.sequenceName}>
            Cadência: {enrollment.sequence.name}
          </p>
        )}

        <div className={styles.statusRow}>
          <span
            className={`${styles.statusBadge} ${styles[`tone_${statusInfo.tone}`]}`}
          >
            <span aria-hidden="true">{statusInfo.icon}</span> {statusInfo.label}
          </span>
          <span className={styles.progressText}>{progressLabel}</span>
          {showNext && (
            <span className={`${styles.nextText} ${next.soon ? styles.nextSoon : ""}`.trim()}>
              · {next.text}
            </span>
          )}
        </div>

        {knownTotal && (
          <div
            className={styles.dots}
            role="img"
            aria-label={`${completed} de ${knownTotal} mensagens enviadas`}
          >
            {Array.from({ length: knownTotal }).map((_, i) => (
              <span
                key={i}
                className={`${styles.dot} ${i < completed ? styles.dotDone : ""}`.trim()}
                aria-hidden="true"
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.side}>
        {enrolledText && <span className={styles.enrolled}>{enrolledText}</span>}
        {(canResume || canCancel) && (
          <div className={styles.actions}>
            {canResume && (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={onResume}
                disabled={busy}
              >
                Retomar
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                className={styles.btnDanger}
                onClick={onCancel}
                disabled={busy}
              >
                Cancelar
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

EnrollmentItem.propTypes = {
  enrollment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    contactType: PropTypes.string,
    contactPhone: PropTypes.string,
    currentStep: PropTypes.number,
    status: PropTypes.string.isRequired,
    nextSendAt: PropTypes.string,
    enrolledAt: PropTypes.string,
    sequence: PropTypes.shape({
      name: PropTypes.string,
      triggerType: PropTypes.string,
    }),
  }).isRequired,
  /** Total de steps da cadência (para os progress dots). null = desconhecido. */
  totalSteps: PropTypes.number,
  onResume: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
